import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    bottomThreshold: { type: Number, default: 80 },
    unreadId: { type: String, default: "" }
  }

  connect() {
    this.opened = false
    this.pinnedToBottom = !this.unreadIdValue
    this.ignoreScroll = false
    this.handleScroll = this.handleScroll.bind(this)
    this.handleSubmitEnd = this.handleSubmitEnd.bind(this)

    this.element.addEventListener("scroll", this.handleScroll, { passive: true })
    document.addEventListener("turbo:submit-end", this.handleSubmitEnd)

    this.scrollToOpenPosition()
    requestAnimationFrame(() => {
      this.scrollToOpenPosition()
      requestAnimationFrame(() => {
        this.scrollToOpenPosition()
        this.finishOpen()
      })
    })

    this.observer = new MutationObserver((mutations) => this.handleMutation(mutations))
    this.observer.observe(this.element, { childList: true })

    this.resizeObserver = new ResizeObserver(() => {
      if (this.opened && this.pinnedToBottom) this.scrollToBottom()
    })
    this.resizeObserver.observe(this.element)
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.handleScroll)
    document.removeEventListener("turbo:submit-end", this.handleSubmitEnd)
    this.observer?.disconnect()
    this.resizeObserver?.disconnect()
  }

  finishOpen() {
    this.opened = true
    this.pinnedToBottom = this.nearBottom()
    this.clearUnreadAnchor()
  }

  clearUnreadAnchor() {
    this.unreadIdValue = ""
    this.element.removeAttribute("data-whatsapp-thread-scroll-unread-id-value")
  }

  handleScroll() {
    if (this.ignoreScroll || !this.opened) return

    this.pinnedToBottom = this.nearBottom()
  }

  handleSubmitEnd(event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return
    if (!this.element.closest(".whatsapp-inbox-shell")?.contains(form)) return

    this.pinnedToBottom = true
    this.scrollToBottom()
  }

  handleMutation(mutations) {
    if (!this.opened) return

    const added = mutations.flatMap((mutation) => [...mutation.addedNodes]).filter((node) => node.nodeType === 1)
    if (!added.length) return

    const sentByUs = added.some((node) => this.isOutboundNode(node))
    if (sentByUs || this.pinnedToBottom) this.scrollToBottom()
  }

  isOutboundNode(node) {
    if (node.classList?.contains("justify-end")) return true

    return Boolean(node.querySelector?.(".justify-end"))
  }

  nearBottom() {
    const { scrollTop, scrollHeight, clientHeight } = this.element

    return scrollHeight - scrollTop - clientHeight <= this.bottomThresholdValue
  }

  scrollToOpenPosition() {
    if (this.scrollToUnread()) return

    this.scrollToBottom()
  }

  scrollToUnread() {
    const target = this.unreadTarget()
    if (!target) return false

    const top = target.getBoundingClientRect().top - this.element.getBoundingClientRect().top + this.element.scrollTop
    const maxScroll = Math.max(this.element.scrollHeight - this.element.clientHeight, 0)

    this.element.scrollTop = top >= maxScroll - 8 ? maxScroll : Math.max(top, 0)
    return true
  }

  unreadTarget() {
    if (!this.unreadIdValue) return null

    return this.element.querySelector(`#whatsapp_message_${this.unreadIdValue}`)
  }

  scrollToBottom() {
    this.ignoreScroll = true
    this.element.scrollTop = this.element.scrollHeight
    this.pinnedToBottom = true
    requestAnimationFrame(() => {
      this.element.scrollTop = this.element.scrollHeight
      this.ignoreScroll = false
    })
  }
}
